import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ShieldCheck, Globe2, Award } from "lucide-react";
import { scrollToId } from "../hooks/useLenis";

const ease = [0.16, 1, 0.3, 1];
const HERO_IMG = "/img/hero-worker.jpg";

const Line = ({ children, delay, className = "" }) => (
  <span className="mask-line">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%", rotate: 2 }}
      animate={{ y: 0, rotate: 0 }}
      transition={{ duration: 1.1, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const Ribbons = ({ y1, y2 }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
    <motion.svg style={{ y: y1 }} className="absolute -top-24 -right-40 w-[1200px] opacity-90" viewBox="0 0 1200 600" fill="none">
      <motion.path
        d="M-50 420 C 250 180, 520 560, 820 300 S 1250 120, 1300 160"
        stroke="#34A853" strokeWidth="22" strokeLinecap="round" strokeOpacity="0.22"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2.2, ease: "easeOut", delay: 0.3 }}
      />
      <motion.path
        d="M-50 470 C 260 230, 540 610, 840 350 S 1250 170, 1300 210"
        stroke="#FF6F00" strokeWidth="14" strokeLinecap="round" strokeOpacity="0.28"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2.4, ease: "easeOut", delay: 0.5 }}
      />
    </motion.svg>
    <motion.div style={{ y: y2 }} className="absolute -left-32 bottom-10 w-[520px] h-[520px] rounded-full bg-safety/10 blur-3xl" />
    <div className="absolute inset-0 topo opacity-70" />
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -160]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <section id="hero" ref={ref} className="relative min-h-screen pt-28 lg:pt-32 pb-16 overflow-hidden bg-white" data-testid="hero-section">
      <Ribbons y1={y1} y2={y2} />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center min-h-[calc(100vh-9rem)]">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2, duration: 0.8, ease }} className="eyebrow mb-6 flex items-center gap-3" data-testid="hero-eyebrow">
            <span className="h-px w-10 bg-african" /> Santé & sécurité au travail · Afrique
          </motion.p>

          <h1 className="font-heading font-extrabold text-forest text-4xl sm:text-5xl lg:text-6xl xl:text-[5.4rem] leading-[0.95] tracking-tight" data-testid="hero-title">
            <Line delay={0.35}>De la Qualité</Line>
            <Line delay={0.5}>
              à <span className="text-gradient-brand">Zéro</span> accident
            </Line>
            <Line delay={0.65} className="font-accent font-normal text-forest/60 text-3xl sm:text-4xl lg:text-5xl mt-3">
              aucune vie n'est négociable.
            </Line>
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9, ease }} className="mt-8 max-w-xl text-base sm:text-lg text-ink/70 leading-relaxed" data-testid="hero-subtitle">
            RisQZero accompagne les entreprises africaines vers une culture sécurité durable : conseil, formation, audit et équipements. Parce que chaque travailleur doit rentrer chez lui, chaque soir.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9, ease }} className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={() => scrollToId("services")} data-testid="hero-cta-services" className="group relative overflow-hidden rounded-full bg-forest text-white font-bold px-7 py-4 text-sm">
              <span className="absolute inset-0 bg-african translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(.16,1,.3,1)]" />
              <span className="relative">Nos solutions HSE</span>
            </button>
            <button onClick={() => scrollToId("impact")} data-testid="hero-cta-stats" className="group inline-flex items-center gap-3 rounded-full border-2 border-forest/15 hover:border-forest text-forest font-bold px-7 py-[14px] text-sm transition-colors">
              Voir les chiffres
              <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
          </motion.div>

          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }} className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-xs font-semibold text-forest/60 uppercase tracking-wider" data-testid="hero-trust">
            <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-safety" /> Normes OIT & ISO 45001</li>
            <li className="flex items-center gap-2"><Globe2 className="w-4 h-4 text-african" /> Expertise terrain Afrique</li>
            <li className="flex items-center gap-2"><Award className="w-4 h-4 text-gold" /> Experts certifiés</li>
          </motion.ul>
        </motion.div>

        <div className="lg:col-span-5 relative" onMouseMove={onMove} onMouseLeave={onLeave} style={{ perspective: 1200 }}>
          <motion.div
            style={{ y: imgY, rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, scale: 0.92, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 1.3, ease }}
            className="relative mx-auto w-full max-w-[460px]"
            data-testid="hero-visual"
          >
            <div className="relative aspect-[4/5] overflow-hidden rounded-[2.5rem] rounded-tr-[9rem] shadow-[0_40px_80px_-30px_rgba(26,79,50,0.45)]">
              <img src={HERO_IMG} alt="Ingénieure HSE africaine sur site industriel" className="absolute inset-0 w-full h-full object-cover object-top scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/80 via-forest/10 to-transparent" />
              <div className="absolute inset-0 grain" />
              <div className="absolute left-32 sm:left-36 bottom-6 right-6 text-white">
                <div className="font-accent text-xs tracking-[0.3em] uppercase text-gold">Mission</div>
                <div className="font-heading font-bold text-xl leading-tight mt-1">Chaque travailleur rentre chez lui en bonne santé.</div>
              </div>
            </div>

            <motion.div
              style={{ translateZ: 80 }}
              className="absolute -left-8 -bottom-10 sm:-left-16 sm:-bottom-12 w-36 h-36 sm:w-44 sm:h-44 rounded-[2rem] bg-white shadow-[0_30px_60px_-20px_rgba(26,79,50,0.4)] border border-forest/10 flex items-center justify-center animate-float"
            >
              <img src="/img/logo.png" alt="Emblème RisQZero" className="w-[78%] h-[78%] object-contain" />
            </motion.div>

            <motion.div
              style={{ translateZ: 60 }}
              initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.4, duration: 0.9, ease }}
              className="absolute -right-4 top-10 sm:-right-10 bg-forest text-white rounded-2xl px-5 py-4 shadow-xl"
              data-testid="hero-chip"
            >
              <div className="font-heading font-extrabold text-3xl leading-none">0</div>
              <div className="text-[0.65rem] uppercase tracking-[0.2em] text-gold mt-1">objectif accident</div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-forest/40 text-[0.6rem] tracking-[0.3em] uppercase">
        Défiler
        <span className="w-px h-10 bg-gradient-to-b from-forest/40 to-transparent" />
      </motion.div>
    </section>
  );
};
